import { useEffect, useRef } from 'react';
import {
  Coachmark,
  type CustomCoachmarkContent,
} from '../../../components/coachmark/coachmark-engine.js';
import '../../../components/coachmark/coachmark.css';

const ICON_ROUTE =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="6" cy="19" r="2"/><circle cx="18" cy="5" r="2"/><path d="M6 17V9a4 4 0 0 1 4-4h6"/></svg>';
const ICON_RADAR =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="4"/><path d="M12 12 L 18 6"/></svg>';
const ICON_STEPS =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 20h5v-5h5v-5h6"/></svg>';
const ICON_SUM =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 19V11M10 19V6M15 19v-5M20 19V9"/></svg>';

/** Las pestañas de Progreso, en orden, con qué muestra cada una (en simple). */
const STEPS: readonly { tab: string; content: Omit<CustomCoachmarkContent, 'eyebrow'> }[] = [
  {
    tab: 'Camino',
    content: {
      accent: 'lime',
      icon: ICON_ROUTE,
      title: 'Tu camino',
      body: 'Cada día que practicás suma una estación. Arriba está hoy; más abajo, lo que hiciste los días anteriores.',
      round: false,
      side: 'below',
    },
  },
  {
    tab: 'Radar',
    content: {
      accent: 'amber',
      icon: ICON_RADAR,
      title: 'El radar',
      body: 'Junta los errores que se te repiten. El coach los trae a la charla hasta que los dejes atrás.',
      round: false,
      side: 'below',
    },
  },
  {
    tab: 'Escalón',
    content: {
      accent: 'purple',
      icon: ICON_STEPS,
      title: 'Tu escalón',
      body: 'Las palabras con las que te habla el coach en tu nivel, y cuáles ya dijiste o conocés.',
      round: false,
      side: 'below',
    },
  },
  {
    tab: 'Totales',
    content: {
      accent: 'lime',
      icon: ICON_SUM,
      title: 'Tus totales',
      body: 'Todo tu recorrido junto: minutos, charlas, tu nivel y cómo vas en cada habilidad.',
      round: false,
      side: 'below',
    },
  },
];

/**
 * El Radar de «Armá tu nave» en Progreso (encargo 117, pedido de Emmanuel): la primera vez que llega
 * con la pieza pendiente, un recorrido corto que señala cada pestaña y dice qué muestra, con los
 * coach-marks de la app. «Siguiente» avanza; tocar afuera lo cierra. La pieza se arma al terminarlo
 * («Entendido» en la última, Emmanuel 20/10); cerrado antes, la barra ofrece volver a verlo. No vuelve
 * una vez armada; sí si repite el recorrido de la nave.
 */
export function ProgressTour({ onEnd }: { onEnd: (finished: boolean) => void }) {
  // El último `onEnd` sin re-armar el recorrido.
  const onEndRef = useRef(onEnd);
  onEndRef.current = onEnd;
  useEffect(() => {
    const container = document.querySelector<HTMLElement>('.cam-root');
    if (!container) return;
    if (getComputedStyle(container).position === 'static') container.style.position = 'relative';
    // Sólo las pestañas que están AHORA. El Escalón se oculta en algunos niveles, y recién cuando llega
    // `/vocab/ladder`: mientras carga se ve. Por eso se miran en cada paso, no al montar (card #577: a
    // B1 y B2 el recorrido decía «3 DE 4 · Tu escalón» y señalaba una pestaña que ya no estaba).
    const stepsNow = () => {
      const tabs = [...container.querySelectorAll<HTMLElement>('.cam-tab')];
      return STEPS.map((s) => ({
        ...s,
        anchor: tabs.find((t) => t.textContent?.trim() === s.tab) ?? null,
      })).filter((s): s is typeof s & { anchor: HTMLElement } => s.anchor !== null);
    };
    const engine = new Coachmark(container);
    let alive = true;
    /** La pestaña que se está mostrando (`null` = todavía ninguna, o terminó). */
    let current: string | null = null;
    // Desde `tab` (o la siguiente que exista, si ésa se fue).
    const show = (tab: string) => {
      if (!alive) return;
      const steps = stepsNow();
      const from = STEPS.findIndex((s) => s.tab === tab);
      const k = steps.findIndex((s) => STEPS.findIndex((x) => x.tab === s.tab) >= from);
      const step = steps[k];
      if (!step) {
        current = null;
        engine.hide();
        // Sin pestañas que señalar no hay recorrido que hacer: cuenta como visto.
        onEndRef.current(true);
        return;
      }
      current = step.tab;
      const last = k === steps.length - 1;
      const next = steps[k + 1]?.tab;
      engine.show({
        anchor: step.anchor,
        content: { ...step.content, eyebrow: `TU PROGRESO · ${k + 1} DE ${steps.length}` },
        cta: last ? 'Entendido' : 'Siguiente',
        onEnd: (how) => {
          if (!alive) return;
          if (how === 'cta' && next) show(next);
          else {
            current = null;
            // Terminado = «Entendido» en la última; cerrarlo antes (tocar afuera) no arma la pieza.
            onEndRef.current(how === 'cta' && last);
          }
        },
      });
    };
    // Si las pestañas cambian con el recorrido abierto, el paso se vuelve a mostrar con la cuenta nueva.
    const tabsList = container.querySelector('.cam-tabs');
    const mo =
      tabsList && typeof MutationObserver !== 'undefined'
        ? new MutationObserver(() => {
            if (current) show(current);
          })
        : null;
    if (tabsList) mo?.observe(tabsList, { childList: true });
    // Un respiro para que la pantalla termine de acomodarse antes de señalar.
    const t = window.setTimeout(() => show(STEPS[0]!.tab), 600);
    return () => {
      alive = false;
      mo?.disconnect();
      window.clearTimeout(t);
      engine.destroy();
    };
  }, []);
  return null;
}
